/**
 * 模态框组件
 *
 * 提供可复用的模态框,支持点击遮罩层关闭、ESC键关闭等功能
 */

import React, { useEffect, useCallback, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

/**
 * 全局Modal打开计数器，用于管理body滚动锁定
 */
let modalOpenCount = 0;

export interface ModalProps {
  /** 是否显示 */
  isOpen: boolean;
  /** 关闭回调 */
  onClose: () => void;
  /** 标题 */
  title?: string;
  /** 内容 */
  children: React.ReactNode;
  /** 底部操作区 */
  footer?: React.ReactNode;
  /** 是否可点击遮罩层关闭 */
  closeOnOverlayClick?: boolean;
  /** 是否可按ESC键关闭 */
  closeOnEsc?: boolean;
  /** 自定义尺寸 */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /** 自定义类名 */
  className?: string;
}

export const Modal: React.FC<ModalProps> = React.memo(({
  isOpen,
  onClose,
  title,
  children,
  footer,
  closeOnOverlayClick = true,
  closeOnEsc = true,
  size = 'md',
  className,
}) => {
  // 使用ref保存滚动位置，确保每个Modal实例独立管理
  const scrollRef = useRef<number>(0);
  // Modal ref用于焦点管理
  const modalRef = useRef<HTMLDivElement>(null);
  // 尺寸配置
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-full h-full m-0 rounded-none'
  };

  // 处理ESC键关闭
  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, closeOnEsc, onClose]);

  // 禁止/恢复背景滚动
  useEffect(() => {
    if (!isOpen) return;

    modalOpenCount++;

    // 只有第一个Modal打开时才锁定body
    if (modalOpenCount === 1) {
      // 计算滚动条宽度，防止布局抖动
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

      // 保存原始 overflow 值
      const originalOverflow = document.body.style.overflow;

      Object.assign(document.body.style, {
        overflow: 'hidden',
        paddingRight: `${scrollbarWidth}px`
      });

      // 保存原始值用于恢复
      scrollRef.current = scrollbarWidth;
      (document.body as any)._originalOverflow = originalOverflow;
    }

    return () => {
      modalOpenCount--;

      // 只有所有Modal都关闭时才恢复body
      if (modalOpenCount === 0) {
        Object.assign(document.body.style, {
          overflow: (document.body as any)._originalOverflow || '',
          paddingRight: ''
        });
        delete (document.body as any)._originalOverflow;
      }
    };
  }, [isOpen]);

  // 焦点陷阱功能
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const modalElement = modalRef.current;
    const focusableElements = modalElement.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const isShiftPressed = e.shiftKey;
      const isFirstFocused = document.activeElement === firstElement;
      const isLastFocused = document.activeElement === lastElement;

      if (isShiftPressed && isFirstFocused) {
        lastElement?.focus();
        e.preventDefault();
      } else if (!isShiftPressed && isLastFocused) {
        firstElement?.focus();
        e.preventDefault();
      }
    };

    document.addEventListener('keydown', handleTab);
    firstElement?.focus();

    return () => document.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  // 处理遮罩层点击
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  }, [closeOnOverlayClick, onClose]);

  // 不显示时返回null
  if (!isOpen) {
    return null;
  }

  const modalClasses = twMerge(
    'bg-white border border-gray-100 rounded-2xl shadow-lg w-full max-h-[90vh] overflow-hidden flex flex-col',
    sizeClasses[size],
    className
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div ref={modalRef} className={modalClasses}>
        {/* 头部 */}
        {title && (
          <div className="flex items-center justify-between p-8 border-b border-gray-100 shrink-0">
            <h2 id="modal-title" className="text-xl font-bold text-gray-900">
              {title}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-900 transition-colors rounded-lg p-1 hover:bg-gray-50"
              aria-label="关闭"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* 内容区域 */}
        <div className="flex-1 overflow-y-auto p-8">
          {children}
        </div>

        {/* 底部操作区 */}
        {footer && (
          <div className="flex justify-end gap-3 p-8 border-t border-gray-100 shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
});

Modal.displayName = 'Modal';

export default Modal;